import React, { useEffect, useRef, useState } from "react";
import { Typography, useTheme } from "@mui/material";
import { Check } from "lucide-react";
import { NavigationStepperProps } from "./types";
import {
  CONNECTOR_MIN_WIDTH,
  STEPPER_GAP,
  StepperContainer,
  StepItem,
  StepCircle,
  StepConnector,
} from "./style";

const NavigationStepperCst: React.FC<NavigationStepperProps> = ({
  steps,
  activeStep,
  onStepChange,
  allowBackNavigation = false,
  allowForwardNavigation = false,
  completedSteps,
  isStepClickable: isStepClickableProp,
}) => {
  const theme = useTheme();
  const activeStepRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scrollable, setScrollable] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const check = () => {
      const items = Array.from(
        el.querySelectorAll<HTMLElement>("[data-step-item]"),
      );
      if (items.length === 0) return;
      const itemsWidth = items.reduce((sum, it) => sum + it.offsetWidth, 0);
      const connectorsCount = items.length - 1;
      const naturalWidth =
        itemsWidth +
        connectorsCount * CONNECTOR_MIN_WIDTH +
        (items.length + connectorsCount - 1) * STEPPER_GAP;
      setScrollable(naturalWidth > el.clientWidth + 1);
    };

    check();
    const observer = new ResizeObserver(check);
    observer.observe(el, { box: "border-box" });
    window.addEventListener("resize", check);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", check);
    };
  }, [steps.length]);

  useEffect(() => {
    activeStepRef.current?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [activeStep, scrollable]);

  const getStepState = (index: number): "active" | "completed" | "pending" => {
    if (index === activeStep) return "active";
    if (completedSteps) {
      return completedSteps.includes(index) ? "completed" : "pending";
    }
    if (index < activeStep) return "completed";
    return "pending";
  };

  const isStepClickable = (index: number): boolean => {
    if (!onStepChange) return false;
    if (index === activeStep) return false;
    if (isStepClickableProp) return isStepClickableProp(index);
    if (index < activeStep) return allowBackNavigation;
    return allowForwardNavigation;
  };

  const handleStepClick = (index: number) => {
    if (!isStepClickable(index)) return;
    onStepChange?.(index);
  };

  return (
    <StepperContainer ref={containerRef} scrollable={scrollable}>
      {steps.map((step, index) => {
        const state = getStepState(index);
        const clickable = isStepClickable(index);
        const isActive = state === "active";

        return (
          <React.Fragment key={step.key}>
            <StepItem
              ref={isActive ? activeStepRef : undefined}
              data-step-item
              clickable={clickable}
              onClick={() => handleStepClick(index)}
            >
              <StepCircle state={state}>
                {state === "completed" && <Check size={12} strokeWidth={3} />}
              </StepCircle>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: isActive ? 700 : 500,
                  lineHeight: 1.25,
                  color: isActive
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
                  whiteSpace: "pre-line",
                }}
              >
                {step.label}
              </Typography>
            </StepItem>
            {index < steps.length - 1 && <StepConnector />}
          </React.Fragment>
        );
      })}
    </StepperContainer>
  );
};

export default NavigationStepperCst;
